import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

import { DEFAULT_MAX_PER_ENTRY } from '@/activity-orchestration/defaults';
import { isActivityShowable, useActivityStore } from '@/store/activity';

import type { IQueueTask, TDisplayTab, TDismissReason } from '@/activity-orchestration/types';

/** 全局串行弹窗队列；任务只引用 ActivityStore 中的活动。 */

/** 优先级相同时保持创建顺序，最后按 id 保证排序稳定。 */
const compareTask = (a: IQueueTask, b: IQueueTask): number =>
    b.priority - a.priority || a.createdAt - b.createdAt || a.activityId.localeCompare(b.activityId);

interface IActivityQueueState {
    /** 等待队列（已按全序排序） */
    waiting: IQueueTask[];
    /** 当前展示中的任务；null 表示空闲 */
    current: IQueueTask | null;
    /**
     * 本会话已自动展示的弹窗数（§5.8：软导航不自动清零，须显式 resetEntry）。
     *
     * 【产品口径 · 整会话只自动弹 1 次（含付费墙挽留）】
     * entryCount 为全站共享计数，非 per-scene。冷启已弹过后，后续 paywall 挽留
     * 入队但 tryShow 被挡是预期行为，不要在进墙 / abandon 时 resetEntry 或 force。
     * 用户主动复弹（挂件点击）走 tryShow({ force: true })，豁免本计数。
     */
    entryCount: number;
    /** scene -> max_per_entry（服务端频控接入前使用默认值） */
    maxPerEntryByScene: Record<string, number>;
    /**
     * 可信的「当前真实所在页面」（§5.2.1 单一真相源）。
     * 由路由层 / 页内浮层维护（Step 3 接入）；tryShow 二次校验只认它，不信任调用方传参。
     */
    activeScene: TDisplayTab | null;
    /**
     * 到墙 offer 拉取（runPaywallOfferOnOpen）落地状态：
     * 付费墙商品卡须等其 settled（成功 / 失败 / 超时兜底）后才展示，避免弹窗晚于墙体到达。
     */
    paywallOfferStatus: TPaywallOfferStatus;
}

/** 到墙 offer 拉取状态：idle 未发起 / pending 在途 / settled 已落地（含失败与超时兜底） */
export type TPaywallOfferStatus = 'idle' | 'pending' | 'settled';

interface IActivityQueueActions {
    /** 入队：全序插入 + 同 mutexGroup 去重替换（§5.1 / §4.2.1） */
    enqueue: (task: IQueueTask) => void;
    /**
     * 尝试展示队首候选（§5.2）。返回 true 表示成功设为 current。
     * force 仅用于用户主动复弹：只豁免 max_per_entry 计数，不绕 current / block（§5.2）。
     */
    tryShow: (tab?: TDisplayTab, opts?: { force?: boolean }) => boolean;
    /** 进入某场景 / 聚合结束：内部 tryShow（§5.1）。校验用可信 activeScene，非本参数（§5.2.1） */
    onScene: (tab: TDisplayTab) => void;
    /**
     * 释放 current 并自动续弹（§5.1）。
     * 幂等：仅当 id 省略或 id === current.id 才生效，防 Modal 关闭动画 / 抢占误清（§5.2.2）。
     * 源 2 落挂件由 PopupHost 侧按 reason==='user' 网关处理（§6.3 / §11 #15），此处只清 current + 续弹。
     */
    dismiss: (reason: TDismissReason, id?: string) => void;
    /**
     * 按 activityId 清队列：去掉 waiting 中同活动任务；若 current 属于该活动则清空并不落挂件，再 tryShow 续弹其它。
     * 供 completeActivity 等业务完成态清场使用。
     */
    purgeActivityTasks: (activityId: string) => void;
    /** 写入某 scene 的 max_per_entry（§4.2 / §4.2.2） */
    setMaxPerEntry: (scene: string, max: number) => void;
    /**
     * 显式重置本会话自动展示计数（§5.8：软导航下不自动清零）。
     * 【口径】整会话只自动弹 1 次时，付费墙 mount / 支付 abandon 不要调此方法，
     * 否则会绕过「含挽留」的会话级限次。冷启聚合结束等明确时机才可 reset。
     */
    resetEntry: (scene?: string) => void;
    /** 更新可信 activeScene（路由 / 浮层层调用，§5.2.1 / §11 #14） */
    setActiveScene: (scene: TDisplayTab | null) => void;
    /** 登录态切换：清空用户相关调度状态，保留当前页面场景 */
    clearForAuthChange: () => void;
    /** 更新到墙 offer 拉取状态（仅 scenes/paywall 调用） */
    setPaywallOfferStatus: (status: TPaywallOfferStatus) => void;
    /** 硬导航 / 重置 */
    reset: () => void;
}

const initialState: IActivityQueueState = {
    waiting: [],
    current: null,
    entryCount: 0,
    maxPerEntryByScene: {},
    activeScene: null,
    paywallOfferStatus: 'idle',
};

export const useActivityQueueStore = create<IActivityQueueState & IActivityQueueActions>()(
    immer((set, get) => ({
        ...initialState,

        enqueue: task =>
            set(state => {
                // 同 mutexGroup 去重：waiting 已存在同组则替换为新任务（newer wins，§4.2.1 / §11 #9）
                if (task.mutexGroup) {
                    state.waiting = state.waiting.filter(t => t.mutexGroup !== task.mutexGroup);
                }
                state.waiting.push(task);
                // 全序重排，保证 tryShow 遍历顺序即优先级顺序
                state.waiting.sort(compareTask);
            }),

        tryShow: (tab, opts) => {
            const force = opts?.force ?? false;
            const { current, waiting, entryCount, maxPerEntryByScene, activeScene } = get();

            // 1. 有 current → 等待 dismiss（force 同样等待）；亦覆盖 block 语义：串行单 current 天然拦截（§5.2 步 1/2 / §5.4 #6）
            if (current) {
                return false;
            }

            // 可信 activeScene 优先；未初始化时回退调用方传参作引导（Step 3 接入后 activeScene 恒有值、始终优先，§5.2.1）
            const scene = activeScene ?? tab ?? null;

            // 3. 非 force 且本会话已达自动展示上限 → 不自动展示
            // 【口径】整会话只自动弹 1 次（含挽留）；冷启已占用名额后挽留 tryShow 失败属预期。
            // force 仅用户主动复弹：不计入、也不被挡（§5.2 步 3 / §5.4 #5）
            const maxPerEntry = scene ? (maxPerEntryByScene[scene] ?? DEFAULT_MAX_PER_ENTRY) : DEFAULT_MAX_PER_ENTRY;
            if (!force && entryCount >= maxPerEntry) {
                return false;
            }

            // 4. 按 priority 取队首候选：过期丢弃、scene 不匹配保留原位、命中出队（§5.2 步 4 / §5.2.1）
            const now = Date.now();
            const { getActivity } = useActivityStore.getState();
            const survivors: IQueueTask[] = [];
            let picked: IQueueTask | null = null;

            waiting.forEach(task => {
                if (picked) {
                    survivors.push(task);
                    return;
                }
                const activity = getActivity(task.activityId);
                if (!isActivityShowable(activity, now)) {
                    // 过期 / 失效：丢弃（不进 survivors），继续试下一条
                    return;
                }
                if (!scene || !task.tabs.includes(scene)) {
                    // scene 不匹配：保留队列原位，等回到对应页由 onScene 再触发（§5.2.1）
                    survivors.push(task);
                    return;
                }
                // 命中：出队（不进 survivors）
                picked = task;
            });

            if (!picked) {
                // 仅当有过期项被丢弃时写回，避免无谓渲染
                if (survivors.length !== waiting.length) {
                    set(state => {
                        state.waiting = survivors;
                    });
                }
                return false;
            }

            set(state => {
                state.waiting = survivors;
                state.current = picked;
                // 非 force 才计数；force 复弹不占名额（§5.2 步 4）
                if (!force) {
                    state.entryCount += 1;
                }
            });
            return true;
        },

        onScene: tab => {
            get().tryShow(tab);
        },

        dismiss: (reason, id) => {
            const { current } = get();
            // 幂等：无 current，或指定 id 与 current 不符 → no-op（§5.2.2）
            if (!current) {
                return;
            }
            if (id != null && id !== current.id) {
                return;
            }
            set(state => {
                state.current = null;
            });
            // replace（晚到抢占）由调用方 force tryShow，此处不自动续弹，避免额外占名额（§5.5）
            if (reason === 'replace') {
                return;
            }
            // 自动续弹：受 max_per_entry 约束（默认 1 时不连弹，§5.4 #7）
            get().tryShow();
        },

        purgeActivityTasks: activityId => {
            const id = activityId?.trim();
            if (!id) {
                return;
            }
            const clearedCurrent = get().current?.activityId === id;
            set(state => {
                state.waiting = state.waiting.filter(task => task.activityId !== id);
                if (state.current?.activityId === id) {
                    state.current = null;
                }
            });
            // 清掉的是当前弹窗时，尝试续弹其它候选（不落挂件）
            if (clearedCurrent) {
                get().tryShow();
            }
        },

        setMaxPerEntry: (scene, max) =>
            set(state => {
                state.maxPerEntryByScene[scene] = max;
            }),

        resetEntry: () =>
            set(state => {
                state.entryCount = 0;
            }),

        setActiveScene: scene =>
            set(state => {
                state.activeScene = scene;
            }),

        clearForAuthChange: () =>
            set(state => {
                state.waiting = [];
                state.current = null;
                state.entryCount = 0;
                state.maxPerEntryByScene = {};
            }),

        setPaywallOfferStatus: status =>
            set(state => {
                state.paywallOfferStatus = status;
            }),

        reset: () => set(() => ({ ...initialState })),
    }))
);
